import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, CheckCircle2, XCircle, Lock, Pause, MessageSquare, Send, Mail, ShieldCheck, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

export default function AdminTransferDetail() {
  const { t: tt } = useLanguage();
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState('');
  const [sendingCode, setSendingCode] = useState(null);
  const [validateCodeValue, setValidateCodeValue] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const load = async () => { const r = await api.get(`/admin/transfers/${id}`); setT(r.data); setMsg(r.data.message_admin || ''); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const action = async (a, motif = '') => {
    await api.post(`/admin/transfers/${id}/action`, { action: a, motif });
    toast.success(`Action: ${a}`); load();
  };
  const sendMsg = async () => {
    await api.post(`/admin/transfers/${id}/message`, { message: msg });
    toast.success('Message envoyé au client');
  };
  const sendCode = async (numero) => {
    setSendingCode(numero);
    try {
      const r = await api.post(`/admin/transfers/${id}/codes/${numero}/send`);
      toast.success(`Code #${numero} envoyé à ${r.data.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSendingCode(null);
    }
  };

  // Admin directly validates a code on behalf of the client (Feature v4)
  const submitValidateCode = async () => {
    const code = (validateCodeValue || '').trim();
    if (!code) { toast.error(tt('admin.codeLabel')); return; }
    setValidatingCode(true);
    try {
      const { data } = await api.post(`/admin/transfers/${id}/validate-code`, { code });
      if (!data.success) {
        toast.error(data.message || tt('admin.codeInvalid'));
        return;
      }
      toast.success(tt('admin.codeValidated'));
      setValidateCodeValue('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tt('admin.codeInvalid'));
    } finally {
      setValidatingCode(false);
    }
  };

  if (!t) return <div>{tt('common.loading')}</div>;
  return (
    <div className="space-y-5">
      <Link to="/admin/virements" className="text-sm text-blue-700 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> {tt('common.back')}</Link>
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{tt('admin.transferDetailTitle')} — {t.beneficiaire_nom}</h1>
          <p className="text-slate-500 text-sm">{t.user_email} • {new Date(t.created_at).toLocaleString('fr-FR')}</p>
        </div>
        <Badge>{t.statut}</Badge>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border p-5 space-y-2">
          <h2 className="font-semibold mb-2">Détails</h2>
          <div className="text-sm"><span className="text-slate-500">Type :</span> <Badge variant={t.type_virement === 'swift' ? 'default' : 'secondary'} className="ml-1">{t.type_virement === 'swift' ? '🌍 SWIFT International' : '🇪🇺 SEPA'}</Badge></div>
          <div className="text-sm"><span className="text-slate-500">Bénéficiaire:</span> {t.beneficiaire_nom}</div>
          <div className="text-sm"><span className="text-slate-500">IBAN:</span> <span className="font-mono text-xs">{t.beneficiaire_iban}</span></div>
          {t.beneficiaire_bic && <div className="text-sm"><span className="text-slate-500">BIC/SWIFT:</span> <span className="font-mono text-xs">{t.beneficiaire_bic}</span></div>}
          <div className="text-sm"><span className="text-slate-500">Montant:</span> <strong>{t.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {t.devise || '€'}</strong></div>
          {t.type_virement === 'swift' && <>
            <div className="text-sm"><span className="text-slate-500">Pays bénéficiaire:</span> {t.pays_beneficiaire || '—'}</div>
            <div className="text-sm"><span className="text-slate-500">Adresse:</span> {t.adresse_beneficiaire || '—'}</div>
            <div className="text-sm"><span className="text-slate-500">Frais:</span> <span className="font-mono">{t.frais || 'SHA'}</span></div>
          </>}
          <div className="text-sm"><span className="text-slate-500">Motif:</span> {t.motif}</div>
          <div className="text-sm"><span className="text-slate-500">Date d'exécution:</span> {t.date_execution}</div>
        </div>
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold mb-3">Codes de validation ({t.codes_valides}/{t.codes_total})</h2>
          <p className="text-xs text-slate-500 mb-3">Cliquez sur ✉️ pour envoyer le code par email au client. Le client recevra le libellé du code et n'a pas besoin d'attendre que le précédent soit validé.</p>
          <div className="space-y-2">
            {t.codes?.map((c) => (
              <div key={c.numero_code} className="flex justify-between items-center gap-2 p-2 rounded bg-slate-50 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">#{c.numero_code} • {c.libelle}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.valide ? `✓ Validé le ${new Date(c.date_validation).toLocaleString('fr-FR')}` :
                     c.date_envoi ? `📨 Envoyé le ${new Date(c.date_envoi).toLocaleString('fr-FR')}` : 'Non envoyé'}
                  </div>
                </div>
                <Button size="sm" variant="outline" disabled={c.valide || sendingCode === c.numero_code}
                  onClick={() => sendCode(c.numero_code)}
                  data-testid={`btn-send-code-${c.numero_code}`}>
                  {sendingCode === c.numero_code ? '...' : <Mail className="w-4 h-4"/>}
                </Button>
              </div>
            ))}
          </div>

          {/* Direct admin code validation (Feature v4) */}
          {t.statut === 'en_attente_validation' && (
            <div className="mt-5 pt-4 border-t">
              <h3 className="text-sm font-semibold flex items-center gap-1 mb-1">
                <ShieldCheck className="w-4 h-4 text-blue-700"/> {tt('admin.directValidateTitle')}
              </h3>
              <p className="text-xs text-slate-500 mb-2">{tt('admin.directValidateHint')}</p>
              <div className="flex gap-2">
                <Input
                  value={validateCodeValue}
                  onChange={(e) => setValidateCodeValue(e.target.value)}
                  placeholder={tt('admin.codeLabel')}
                  className="font-mono tracking-widest"
                  disabled={validatingCode}
                  data-testid="input-admin-validate-code"
                />
                <Button
                  onClick={submitValidateCode}
                  disabled={validatingCode || !validateCodeValue.trim()}
                  className="bg-blue-700 shrink-0"
                  data-testid="btn-admin-validate-code"
                >
                  <KeyRound className="w-4 h-4 mr-1"/>
                  {validatingCode ? '...' : tt('admin.validateCode')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-5">
        <h2 className="font-semibold flex items-center gap-2 mb-3"><MessageSquare className="w-5 h-5"/> Message pour le client</h2>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} data-testid="input-admin-msg"/>
        <Button onClick={sendMsg} className="mt-3 bg-blue-700" data-testid="btn-send-admin-msg"><Send className="w-4 h-4 mr-1"/> Envoyer le message</Button>
      </div>

      <div className="bg-white rounded-2xl border p-5">
        <h2 className="font-semibold mb-3">Actions</h2>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => action('valider')} className="bg-blue-700" data-testid="btn-action-valider"><CheckCircle2 className="w-4 h-4 mr-1"/> Valider</Button>
          <Button onClick={() => action('executer')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-action-executer">Exécuter (débiter)</Button>
          <Button variant="destructive" onClick={() => action('annuler')} data-testid="btn-action-annuler"><XCircle className="w-4 h-4 mr-1"/> Annuler</Button>
          <Button variant="outline" onClick={() => action('refuser')} data-testid="btn-action-refuser">Refuser</Button>
          <Button variant="outline" onClick={() => action('bloquer')} data-testid="btn-action-bloquer"><Lock className="w-4 h-4 mr-1"/> Bloquer</Button>
          <Button variant="outline" onClick={() => action('suspendre')} data-testid="btn-action-suspendre"><Pause className="w-4 h-4 mr-1"/> Suspendre</Button>
        </div>
      </div>
    </div>
  );
}
